import { captureException } from '@sentry/nextjs';
import { IReceipt } from '../interface/receipt.interface';
import axios from 'axios';
import querystring from 'qs';
import { httpKeepAliveAgent, httpsKeepAliveAgent } from '../helpers/utils/connectionPoolConfig';

const BASE_URL = process.env.NEXT_PUBLIC_RECEIPT_BASE_URL;
const TIMEOUT = 15000;

const api = axios.create({
  httpAgent: httpKeepAliveAgent,
  httpsAgent: httpsKeepAliveAgent,
  baseURL: BASE_URL,
  timeout: TIMEOUT,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  paramsSerializer: (params) => querystring.stringify(params),
});

const APIInstance = {
  /**
   * @param {Sring} url '/path/to/endpoint'
   * @param {Object} param query params
   */
  get: (url, customConfig = {}) => {
    return api.get(url, {
      ...customConfig,
    });
  },

  /**
   * @param {Sring} url '/path/to/endpoint'
   * @param {Object} json
   * @param {Object} form
   * @param {Object} reqConfig  custom config for request
   */
  post: (url, form = null, json = {}, reqConfig = {}) => {
    api.defaults.headers['Content-Type'] = form
      ? 'application/x-www-form-urlencoded'
      : 'application/json';

    const data = querystring.stringify(form) || json;
    return api.post(url, data, {
      params: querystring.stringify(form),
      ...reqConfig,
    });
  },
};

api.interceptors.response.use(
  async (response) => response,
  async function (error) {
    // capture error to sentry
    captureException(error);

    const originalRequest = error.config;
    if (error?.response?.status === 401) {
      try {
        originalRequest._retry = true;
        const res = await getToken();
        originalRequest.headers['Authorization'] = `Bearer ${res.data.access_token}`;
        return api(originalRequest);
      } catch (err) {
        console.log(err);
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

const getReceipt = (token: string, code: string): Promise<{ data: IReceipt }> => {
  return APIInstance.get('/auto2000commercewebservices/v2/auto2000/qr-code/eseal-info', {
    params: { code },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

const getToken = (): Promise<{
  data: {
    access_token: string;
    token_type: string;
    expires_in: number;
    scope: string;
  };
}> => {
  return APIInstance.post('/authorizationserver/oauth/token', {
    grant_type: 'client_credentials',
    client_id: process.env.RECEIPT_CLIENT_ID,
    client_secret: process.env.RECEIPT_CLIENT_SECRET,
  });
};

export default {
  getReceipt,
  getToken,
};
